import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { PriceInfoView } from '@euler/app/flows/task/pages/quotation/components/quotation-form/PriceInfoView';
import {
  kMaterialTypes,
  kQuotationMaterialInventoryStatusInfoMap,
} from '@euler/app/flows/task/pages/quotation/functions/constants';
import { Center } from '@euler/components/Center';
import { Checkbox } from '@euler/components/form/Checkbox';
import { Stepper } from '@euler/components/form/Stepper';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { QuotationMaterialItemStaged } from '@euler/model/task-detail';
import { withConfirmation } from '@euler/utils';
import { memo, useCallback } from 'react';
import { StyleProp, ViewStyle } from 'react-native';

const formatQty = (qty: number) => `x ${qty}`;

export const MaterialItemView = memo(
  ({
    editable,
    index,
    material,
    style,
    onEdit,
    onChange,
    onRemove,
  }: {
    editable?: boolean;
    material: QuotationMaterialItemStaged;
    index: number;
    style?: StyleProp<ViewStyle>;
    onEdit?: (index: number, material: QuotationMaterialItemStaged) => void;
    onChange?: (index: number, material: QuotationMaterialItemStaged) => void;
    onRemove?: (index: number, material: QuotationMaterialItemStaged) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const onCheckChange = useCallback(
      (checked: boolean) => {
        onChange?.(index, { ...material, included: checked });
      },
      [index, material, onChange],
    );

    const onQtyChange = useCallback(
      (value: number) => {
        onChange?.(index, { ...material, qty: value });
      },
      [index, material, onChange],
    );

    const onRemovePress = useCallback(async () => {
      if (await withConfirmation({ title: '确定删除该配件？' })) {
        onRemove?.(index, material);
      }
    }, [index, material, onRemove]);

    const onEditPress = useCallback(() => {
      onEdit?.(index, material);
    }, [index, material, onEdit]);

    return (
      <StackLayout
        direction="row"
        style={style}
        gap={rpx(4)}
        alignItems="center"
      >
        {editable && (
          <Checkbox
            shape="circle"
            checked={material.included}
            size={rpx(20)}
            checkedColor={'#555'}
            onChange={onCheckChange}
            css={`
              align-self: flex-start;
              top: ${rpx(2)}px;
            `}
          />
        )}
        <StackLayout flex={1} gap={rpx(4)}>
          <StackLayout
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            gap={rpx(5)}
          >
            <StackLayout
              direction="row"
              alignItems="center"
              gap={rpx(2)}
              flex={1}
            >
              <Typography.Text
                regular
                color="#444"
                size={rpx(16)}
                lineHeight={rpx(24)}
              >
                {material.name}
              </Typography.Text>
              {material.partNumber?.trim() ? (
                <Typography.Text
                  light
                  opacity={0.5}
                  size={rpx(13)}
                  lineHeight={rpx(24)}
                >{`(配件号: ${material.partNumber.trim()})`}</Typography.Text>
              ) : null}
            </StackLayout>
            {editable && (
              <>
                <Center.Touchable onPress={onEditPress}>
                  <Icon name="note" size={rpx(16)} color="#777" />
                </Center.Touchable>
                <Center.Touchable onPress={onRemovePress}>
                  <Icon name="trash-o" size={rpx(16)} color="#777" />
                </Center.Touchable>
              </>
            )}
          </StackLayout>
          {material.brandName?.trim().length ||
          material.inventoryStatus != null ||
          material.materialType != null ? (
            <StackLayout direction="row" alignItems="center" gap={rpx(4)}>
              {material.materialType != null ? (
                <Tag
                  label={
                    kMaterialTypes[material.materialType]?.name ??
                    material.materialType
                  }
                />
              ) : null}
              {material.brandName?.trim().length ? (
                <Tag label={material.brandName} />
              ) : null}
              {material.inventoryStatus != null ? (
                <Tag
                  label={
                    kQuotationMaterialInventoryStatusInfoMap[
                      material.inventoryStatus
                    ]?.name ?? material.inventoryStatus
                  }
                />
              ) : null}
            </StackLayout>
          ) : null}
          <HStack>
            <HStack
              justifyContent="flex-start"
              css={`
                flex: 1;
              `}
            >
              <Typography.Text color="#777" flexShrink={0} numberOfLines={1}>
                配件费:{' '}
              </Typography.Text>
              <PriceInfoView
                price={material.price}
                discountPrice={material.discountPrice}
                discountRemark={material.discountRemark}
              />
            </HStack>
            <Stepper
              editable={editable}
              value={material.qty ?? 1}
              min={1}
              max={1000}
              format={formatQty}
              inputStyle={{ maxWidth: rpx(20) }}
              onChange={onQtyChange}
              css={`
                margin-right: ${rpx(-2)}px;
              `}
            />
          </HStack>
          {material.remark ? (
            <StackLayout direction="row">
              <Typography.Text color="#777" flexShrink={0} numberOfLines={1}>
                备注:{' '}
              </Typography.Text>
              <Typography.Text>{material.remark}</Typography.Text>
            </StackLayout>
          ) : null}
        </StackLayout>
      </StackLayout>
    );
  },
);

const Tag = memo(
  ({ label, style }: { label: string; style?: StyleProp<ViewStyle> }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Center
        style={style}
        css={`
          background-color: #eee;
          padding: ${rpx(2)}px ${rpx(5)}px;
          border-radius: ${rpx(3)}px;
        `}
      >
        <Typography.Text light size={rpx(12)} color="#555">
          {label}
        </Typography.Text>
      </Center>
    );
  },
);
